import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import TaskForm from './TaskForm';
import TaskCreateModal from '../TaskCreateModal';
import api from '../../services/api';
import toast from '../../utils/toast';
import 'react-toastify/dist/ReactToastify.css';
import { generateCustomIdsForList } from '../../utils/customIdGenerator';
import { 
  CheckIcon, 
  XMarkIcon, 
  ClockIcon, 
  UserIcon, 
  CalendarIcon,
  DocumentIcon,
  EyeIcon,
  TrashIcon,
  PlusIcon,
  MinusIcon,
  ArrowDownTrayIcon,
  FunnelIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Squares2X2Icon,
  BookmarkIcon,
  StarIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import PasteUploadArea from '../PasteUploadArea';
import { createVerificationRecord } from '../../utils/documentVerification';
import { exportToExcel, exportToPDF } from '../../utils/exportUtils';
import ExportButtonsWithAll from '../ExportButtonsWithAll';

const TaskList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isVerificationModalVisible, setIsVerificationModalVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [verificationFields, setVerificationFields] = useState({});
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [verificationTypes, setVerificationTypes] = useState([]);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Sorting state
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');

  // Filter state
  const [filters, setFilters] = useState({
    status: null,
    client: null,
    assignedTo: null,
    priority: null,
    dateRange: null
  });

  // Header filter state
  const [headerFilters, setHeaderFilters] = useState({});
  const [showHeaderFilters, setShowHeaderFilters] = useState({});
  const [filterInputValues, setFilterInputValues] = useState({});
  const [dateRangeFilters, setDateRangeFilters] = useState({});
  const [filterDialog, setFilterDialog] = useState({ open: false, field: null });
  const [selectedStatuses, setSelectedStatuses] = useState([]);

  // Column visibility state
  const [showColumnVisibility, setShowColumnVisibility] = useState(false);
  const [columnVisibilityPosition, setColumnVisibilityPosition] = useState({ top: null, right: null });
  const columnVisibilityButtonRef = useRef(null);
  const [columnVisibility, setColumnVisibility] = useState({
    customId: true,
    name: true,
    client: true,
    assignedTo: true,
    assignedBy: true,
    deadline: true,
    status: true,
    priority: true,
    description: false,
    createdAt: false
  });

  // User Views state
  const [savedViews, setSavedViews] = useState([]);
  const [showSaveViewDialog, setShowSaveViewDialog] = useState(false);
  const [showLoadViewDialog, setShowLoadViewDialog] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [savingView, setSavingView] = useState(false);
  const [loadingViews, setLoadingViews] = useState(false);
  const [currentViewId, setCurrentViewId] = useState(null);
  const [taskStats, setTaskStats] = useState({ statusCounts: {}, deadlineCounts: {} });
  const [statusUpdatingTaskId, setStatusUpdatingTaskId] = useState(null);
  const initialSortSetRef = useRef(false);

  useEffect(() => {
    fetchTasks();
    fetchClients();
    fetchUsers();
    fetchGroups();
    fetchVerificationTypes();
    fetchSavedViews();
    loadDefaultView();
    fetchTaskStats();
  }, []);

  const fetchTaskStats = async () => {
    try {
      const response = await api.get('/tasks/stats');
      if (response.data) {
        setTaskStats({
          statusCounts: response.data.statusCounts || {},
          deadlineCounts: response.data.deadlineCounts || {}
        });
      }
    } catch (error) {
      console.error('Error fetching task stats:', error);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, [pagination.page, pagination.limit, selectedStatuses, sortField, sortDirection, headerFilters, dateRangeFilters]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        sortField,
        sortDirection,
        ...headerFilters,
        ...filters
      };

      // Add status filter
      if (selectedStatuses.length > 0) {
        params.status = selectedStatuses;
      }

      // Add date range filters
      Object.keys(dateRangeFilters).forEach(key => {
        if (dateRangeFilters[key].from) params[`${key}_from`] = dateRangeFilters[key].from;
        if (dateRangeFilters[key].to) params[`${key}_to`] = dateRangeFilters[key].to;
      });

      const response = await api.get('/tasks', { params });
      const tasksWithCustomIds = await generateCustomIdsForList('Tasks', response.data.data || response.data);
      setTasks(tasksWithCustomIds);
      
      // Update pagination if backend returns it
      if (response.data.pagination) {
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.total,
          pages: response.data.pagination.pages
        }));
      }
    } catch (error) {
      toast.error('Failed to fetch tasks');
    } finally {
      setLoading(false);
      setFilterLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/master-data');
      setClients(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch clients');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await api.get('/groups');
      setGroups(response.data);
    } catch (error) {
      toast.error('Failed to fetch groups');
    }
  };

  const fetchVerificationTypes = async () => {
    try {
      const response = await api.get('/admin/verification-types');
      setVerificationTypes(response.data);
    } catch (error) {
      toast.error('Failed to fetch verification types');
    }
  };

  // Fetch saved views
  const fetchSavedViews = async () => {
    try {
      setLoadingViews(true);
      const response = await api.get('/user-views/tasks');
      if (response.data.success) {
        setSavedViews(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching saved views:', error);
    } finally {
      setLoadingViews(false);
    }
  };

  // Load default view on page load
  const loadDefaultView = async () => {
    try {
      const response = await api.get('/user-views/tasks/default');
      if (response.data.success && response.data.data) {
        const defaultView = response.data.data;
        applyView(defaultView, true);
      }
    } catch (error) {
      console.error('Error loading default view:', error);
    }
  };

  // Apply a view's settings
  const applyView = (view, silent = false) => {
    if (!view || !view.viewSettings) return;
    
    const { columnVisibility: viewColumnVisibility, sortField: viewSortField, sortDirection: viewSortDirection } = view.viewSettings;
    
    initialSortSetRef.current = true;
    
    // Apply column visibility
    if (viewColumnVisibility) {
      const visibilityObj = {};
      if (viewColumnVisibility instanceof Map) {
        viewColumnVisibility.forEach((value, key) => {
          visibilityObj[key] = value;
        });
      } else {
        Object.assign(visibilityObj, viewColumnVisibility);
      }
      setColumnVisibility(visibilityObj);
    }
    
    // Apply sorting
    if (viewSortField) setSortField(viewSortField);
    if (viewSortDirection) setSortDirection(viewSortDirection);
    
    setCurrentViewId(view._id);
    if (!silent) {
      toast.success(`View "${view.name}" loaded`);
    }
  };

  // Save current view
  const handleSaveView = async () => {
    if (!newViewName.trim()) {
      toast.error('Please enter a view name');
      return;
    }

    try {
      setSavingView(true);
      const viewSettings = {
        columnVisibility,
        sortField,
        sortDirection
      };

      const response = await api.post('/user-views/tasks', {
        name: newViewName.trim(),
        viewSettings,
        isDefault: saveAsDefault
      });

      if (response.data.success) {
        toast.success('View saved successfully');
        setShowSaveViewDialog(false);
        setNewViewName('');
        setSaveAsDefault(false);
        fetchSavedViews();
        if (saveAsDefault) {
          setCurrentViewId(response.data.data._id);
        }
      }
    } catch (error) {
      console.error('Error saving view:', error);
      toast.error(error.response?.data?.message || 'Failed to save view');
    } finally {
      setSavingView(false);
    }
  };

  // Load a saved view
  const handleLoadView = (view) => {
    applyView(view);
    setShowLoadViewDialog(false);
  };

  // Delete a saved view
  const handleDeleteView = async (viewId) => {
    if (!window.confirm('Are you sure you want to delete this view?')) {
      return;
    }

    try {
      const response = await api.delete(`/user-views/${viewId}`);
      if (response.data.success) {
        toast.success('View deleted successfully');
        fetchSavedViews();
        if (currentViewId === viewId) {
          setCurrentViewId(null);
        }
      }
    } catch (error) {
      console.error('Error deleting view:', error);
      toast.error('Failed to delete view');
    }
  };

  // Set a view as default
  const handleSetDefaultView = async (viewId) => {
    try {
      const response = await api.put(`/user-views/${viewId}/default`);
      if (response.data.success) {
        toast.success('Default view set successfully');
        fetchSavedViews();
      }
    } catch (error) {
      console.error('Error setting default view:', error);
      toast.error('Failed to set default view');
    }
  };

  // Sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ChevronUpIcon className="h-4 w-4 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ChevronUpIcon className="h-4 w-4 text-blue-600" />
      : <ChevronDownIcon className="h-4 w-4 text-blue-600" />;
  };

  // Fetch all tasks for export (without pagination)
  const fetchAllTasks = async () => {
    try {
      console.log('📡 Fetching ALL tasks for export...');
      const response = await api.get('/tasks', { 
        params: {
          ...filters,
          limit: 999999
        }
      });
      const tasksWithCustomIds = await generateCustomIdsForList('Tasks', response.data.data || response.data);
      
      const exportData = tasksWithCustomIds.map(task => ({
        ...task,
        assignedToNames: [
          ...(task.assignedTo || []).map(u => `${u.firstName} ${u.lastName}`),
          ...(task.assignedGroups || []).map(g => `Group: ${g.name}`)
        ].join(', '),
        deadline: task.deadline ? new Date(task.deadline).toLocaleDateString() : 'N/A'
      }));
      
      console.log(`✅ Fetched ${exportData.length} tasks for export`);
      return exportData;
    } catch (error) {
      console.error('❌ Error fetching all tasks:', error);
      toast.error('Failed to fetch all tasks');
      throw error;
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    setStatusUpdatingTaskId(taskId);
    try {
      if (newStatus === 'completed') {
        const task = tasks.find(t => t._id === taskId);
        
        if (task.verificationTypes && task.verificationTypes.length > 0) {
          const areAllFieldsFilled = task.verificationTypes.every(vt => {
            const verificationType = verificationTypes.find(v => v._id === vt.id);
            if (!verificationType) return true;
            
            const fieldValue = task.verificationData?.[verificationType.label];
            return fieldValue && fieldValue.trim() !== '';
          });
          
          if (areAllFieldsFilled) {
            await api.patch(`/tasks/${taskId}/status`, {
              status: newStatus,
              verificationData: task.verificationData
            });
            toast.success('Task completed successfully');
            fetchTasks();
            fetchTaskStats();
            return;
          }
        }
        
        setStatusUpdatingTaskId(null);
        setSelectedTask(task);
        setPendingStatusChange(newStatus);
        setVerificationFields({});
        sendCode(taskId);
        setIsVerificationModalVisible(true);
      } else {
        await api.patch(`/tasks/${taskId}/status`, { status: newStatus });
        toast.success('Task status updated successfully');
        fetchTasks();
        fetchTaskStats();
      }
    } catch (error) {
      toast.error('Failed to update task status');
    } finally {
      setStatusUpdatingTaskId(null);
    }
  };

  const handleVerificationSubmit = async () => {
    try {
      const task = selectedTask;
      const requiredFields = task.verificationTypes || [];

      const missingFields = requiredFields.filter(field => {
        const verificationType = verificationTypes.find(vt => vt._id === field.id);
        return (verificationType?.type !== 'otp-email' && !verificationFields[verificationType?.label]);
      });

      if (missingFields.length > 0) {
        toast.error(`Please fill all verification fields: ${missingFields.join(', ')}`);
        return;
      }

      await api.patch(`/tasks/${task._id}/status`, {
        status: pendingStatusChange,
        verificationData: verificationFields
      });

      setIsVerificationModalVisible(false);
      setPendingStatusChange(null);
      setSelectedTask(null);
      setVerificationFields({});
      toast.success('Task completed successfully');
      fetchTasks();
      fetchTaskStats();

    } catch (error) {
      toast.error('Failed to complete task verification\n' + error.response?.data?.message);
    }
  };

  const sendCode = async (taskId) => {
    try {
      await api.post(`/tasks/${taskId}/generateOTP`);
      toast.success('OTP code sent successfully to your email');
    } catch (error) {
      toast.error('Failed to send OTP code');
    }
  };

  const handleTaskClick = (task) => {
    navigate(`/tasks/${task._id}`);
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilterLoading(true);
    setHeaderFilters({});
    setSelectedStatuses([]);
    setDateRangeFilters({});
    setFilterInputValues({});
    setFilters({
      status: null,
      client: null,
      assignedTo: null,
      priority: null,
      dateRange: null
    });
  };

  // Define table fields
  const getTableFields = () => {
    const allFields = [
      { key: 'customId', label: 'Custom ID', sortable: true, filterable: true, fieldType: 'text' },
      { key: 'name', label: 'Task Name', sortable: true, filterable: true, fieldType: 'text' },
      { 
        key: 'client', 
        label: 'Client', 
        sortable: true, 
        filterable: true, 
        fieldType: 'dropdown', 
        options: clients.map(c => ({ value: c._id, label: c.clientName }))
      },
      { key: 'assignedTo', label: 'Assigned To', sortable: false, filterable: false, fieldType: 'text' },
      { 
        key: 'assignedBy', 
        label: 'Assigned By', 
        sortable: true, 
        filterable: true, 
        fieldType: 'dropdown', 
        options: users.map(u => ({ value: u._id, label: `${u.firstName} ${u.lastName}` }))
      },
      { key: 'deadline', label: 'Deadline', sortable: true, filterable: true, fieldType: 'date' },
      { key: 'status', label: 'Status', sortable: true, filterable: true, fieldType: 'dropdown', options: ['pending', 'in_progress', 'completed', 'cancelled'] },
      { key: 'priority', label: 'Priority', sortable: true, filterable: true, fieldType: 'dropdown', options: ['low', 'medium', 'high', 'urgent'] },
      { key: 'description', label: 'Description', sortable: false, filterable: true, fieldType: 'text' },
      { key: 'createdAt', label: 'Created Date', sortable: true, filterable: true, fieldType: 'date' }
    ];

    return allFields.filter(field => columnVisibility[field.key] !== false);
  };

  const getAllTableFields = () => {
    return [
      { key: 'customId', label: 'Custom ID' },
      { key: 'name', label: 'Task Name' },
      { key: 'client', label: 'Client' },
      { key: 'assignedTo', label: 'Assigned To' },
      { key: 'assignedBy', label: 'Assigned By' },
      { key: 'deadline', label: 'Deadline' },
      { key: 'status', label: 'Status' },
      { key: 'priority', label: 'Priority' },
      { key: 'description', label: 'Description' },
      { key: 'createdAt', label: 'Created Date' }
    ];
  };

  // Sortable Header Component
  const SortableHeader = ({ field, onSort }) => {
    const isFilterActive = (field.fieldType === 'date' 
      ? (dateRangeFilters[field.key] && (dateRangeFilters[field.key].from || dateRangeFilters[field.key].to))
      : field.fieldType === 'dropdown'
      ? (Array.isArray(headerFilters[field.key]) ? headerFilters[field.key].length > 0 : headerFilters[field.key] && typeof headerFilters[field.key] === 'string' && headerFilters[field.key].trim() !== '')
      : (headerFilters[field.key] && typeof headerFilters[field.key] === 'string' && headerFilters[field.key].trim() !== ''));
    
    return (
      <div className="flex items-center justify-between">
        <button
          onClick={() => field.sortable && onSort(field.key)}
          className={`flex items-center space-x-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
            field.sortable ? 'cursor-pointer hover:text-gray-700' : 'cursor-default'
          }`}
        >
          <span className="text-xs">{field.label}</span>
          {field.sortable && getSortIcon(field.key)}
        </button>
        
        {field.filterable && (
          <button
            onClick={() => setFilterDialog({ open: true, field })}
            className={`p-1 rounded ${
              isFilterActive ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'
            }`}
            title="Filter"
          >
            <FunnelIcon className="h-3 w-3" />
          </button>
        )}
      </div>
    );
  };

  // Filter Dialog Component
  const FilterDialog = ({ field, onClose }) => {
    const [localFilterValue, setLocalFilterValue] = useState('');
    const [localSelectedOptions, setLocalSelectedOptions] = useState([]);
    const [localDateRange, setLocalDateRange] = useState({ from: '', to: '' });
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
      if (field) {
        if (field.fieldType === 'date') {
          const range = dateRangeFilters[field.key] || { from: '', to: '' };
          setLocalDateRange(range);
        } else if (field.fieldType === 'dropdown') {
          const filterValue = headerFilters[field.key];
          if (Array.isArray(filterValue)) {
            setLocalSelectedOptions(filterValue);
          } else {
            setLocalSelectedOptions([]);
          }
        } else {
          setLocalFilterValue(headerFilters[field.key] || '');
        }
      }
    }, [field]);

    const handleApply = () => {
      setFilterLoading(true);
      if (field.fieldType === 'date') {
        setDateRangeFilters(prev => ({
          ...prev,
          [field.key]: localDateRange
        }));
      } else if (field.fieldType === 'dropdown') {
        if (localSelectedOptions.length > 0) {
          // For client and assignedBy, store the IDs directly
          setHeaderFilters(prev => ({
            ...prev,
            [field.key]: localSelectedOptions
          }));
        } else {
          const newFilters = { ...headerFilters };
          delete newFilters[field.key];
          setHeaderFilters(newFilters);
        }
      } else {
        if (localFilterValue.trim()) {
          setHeaderFilters(prev => ({
            ...prev,
            [field.key]: localFilterValue
          }));
        } else {
          const newFilters = { ...headerFilters };
          delete newFilters[field.key];
          setHeaderFilters(newFilters);
        }
      }
      onClose();
    };

    const handleClear = () => {
      setFilterLoading(true);
      if (field.fieldType === 'date') {
        const newFilters = { ...dateRangeFilters };
        delete newFilters[field.key];
        setDateRangeFilters(newFilters);
      } else {
        const newFilters = { ...headerFilters };
        delete newFilters[field.key];
        setHeaderFilters(newFilters);
      }
      onClose();
    };

    const toggleOption = (option) => {
      // Handle both simple string options and object options with value/label
      const optionValue = typeof option === 'object' ? option.value : option;
      setLocalSelectedOptions(prev => 
        prev.includes(optionValue) 
          ? prev.filter(o => o !== optionValue)
          : [...prev, optionValue]
      );
    };

    if (!field) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Filter: {field.label}
          </h3>

          {field.fieldType === 'date' ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                <input
                  type="date"
                  value={localDateRange.from}
                  onChange={(e) => setLocalDateRange(prev => ({ ...prev, from: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                <input
                  type="date"
                  value={localDateRange.to}
                  onChange={(e) => setLocalDateRange(prev => ({ ...prev, to: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          ) : field.fieldType === 'dropdown' ? (
            <div className="space-y-3">
              {/* Show search field for client and assignedBy */}
              {(field.key === 'client' || field.key === 'assignedBy') && (
                <div className="relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={`Search ${field.label.toLowerCase()}...`}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    autoFocus
                  />
                </div>
              )}
              
              {/* Selected items display */}
              {localSelectedOptions.length > 0 && (
                <div className="flex flex-wrap gap-2 p-2 bg-gray-50 rounded-md">
                  {localSelectedOptions.map(optionValue => {
                    const option = field.options?.find(opt => 
                      (typeof opt === 'object' ? opt.value : opt) === optionValue
                    );
                    const optionLabel = option 
                      ? (typeof option === 'object' ? option.label : option.replace('_', ' '))
                      : optionValue;
                    
                    return (
                      <span 
                        key={optionValue} 
                        className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-sm"
                      >
                        {optionLabel}
                        <button
                          onClick={() => toggleOption(option || optionValue)}
                          className="ml-1 text-blue-600 hover:text-blue-800"
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              
              {/* Options list */}
              <div className="space-y-1 max-h-64 overflow-y-auto border border-gray-200 rounded-md p-2">
                {field.options && field.options
                  .filter(option => {
                    if (!searchTerm) return true;
                    const optionLabel = typeof option === 'object' ? option.label : option;
                    return optionLabel.toLowerCase().includes(searchTerm.toLowerCase());
                  })
                  .map(option => {
                    // Handle both simple string options and object options with value/label
                    const optionValue = typeof option === 'object' ? option.value : option;
                    const optionLabel = typeof option === 'object' ? option.label : option.replace('_', ' ');
                    
                    return (
                      <label key={optionValue} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                        <input
                          type="checkbox"
                          checked={localSelectedOptions.includes(optionValue)}
                          onChange={() => toggleOption(option)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{optionLabel}</span>
                      </label>
                    );
                  })}
                {field.options && field.options.filter(option => {
                  if (!searchTerm) return true;
                  const optionLabel = typeof option === 'object' ? option.label : option;
                  return optionLabel.toLowerCase().includes(searchTerm.toLowerCase());
                }).length === 0 && (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    No matches found
                  </div>
                )}
              </div>
            </div>
          ) : (
            <input
              type="text"
              value={localFilterValue}
              onChange={(e) => setLocalFilterValue(e.target.value)}
              placeholder={`Search ${field.label.toLowerCase()}...`}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          )}

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={handleClear}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Clear
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    );
  };

  const tableFields = getTableFields();

  return (
    <div className="space-y-3">
      {/* Dashboard: Status + Deadline counts */}
      <div className="bg-white rounded-lg shadow p-4 overflow-hidden">
        <div className="dashboard-scroll flex items-center gap-6 overflow-x-auto pb-2" style={{ scrollbarWidth: 'thin' }}>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-sm font-semibold text-gray-700">Status:</span>
            <div className="flex flex-wrap items-center gap-2">
              {taskStats.statusCounts && Object.entries(taskStats.statusCounts).length > 0 ? (
                Object.entries(taskStats.statusCounts).map(([status, count]) => (
                  <span key={status} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800">
                    {status.replace(/_/g, ' ')}: <strong className="ml-1">{count}</strong>
                  </span>
                ))
              ) : (
                <span className="text-sm text-gray-500">—</span>
              )}
            </div>
          </div>
          <span className="text-gray-300 shrink-0">|</span>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-sm font-semibold text-gray-700">Deadline:</span>
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-red-50 text-red-800">
                Overdue: <strong className="ml-1">{taskStats.deadlineCounts?.overdue ?? 0}</strong>
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-amber-50 text-amber-800">
                Today: <strong className="ml-1">{taskStats.deadlineCounts?.today ?? 0}</strong>
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-50 text-blue-800">
                Next 7 days: <strong className="ml-1">{taskStats.deadlineCounts?.next7 ?? 0}</strong>
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800">
                Next 30 days: <strong className="ml-1">{taskStats.deadlineCounts?.next30 ?? 0}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Top Action Bar */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {/* Clear Filters Button */}
              {(Object.keys(headerFilters).length + selectedStatuses.length + Object.keys(dateRangeFilters).length > 0) && (
                <button
                  onClick={clearAllFilters}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md relative"
                  title="Clear All Filters"
                >
                  <FunnelIcon className="h-5 w-5" />
                  <span className="absolute -top-1 -right-1 h-5 w-5 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center">
                    {(Object.keys(headerFilters).length + selectedStatuses.length + Object.keys(dateRangeFilters).length)}
                  </span>
                </button>
              )}
            </div>

            <div className="flex items-center space-x-2">
              {/* Save View Button */}
              <button
                onClick={() => setShowSaveViewDialog(true)}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
                title="Save Current View"
              >
                <BookmarkIcon className="h-5 w-5" />
              </button>

              {/* Load View Button */}
              <button
                onClick={() => setShowLoadViewDialog(true)}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md relative"
                title="Load Saved View"
              >
                <StarIcon className="h-5 w-5" />
                {savedViews.length > 0 && (
                  <span className="absolute top-0 right-0 h-4 w-4 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center">
                    {savedViews.length}
                  </span>
                )}
              </button>

              {/* Column Visibility Icon */}
              <div className="relative column-visibility-dropdown">
                <button
                  ref={columnVisibilityButtonRef}
                  onClick={() => {
                    if (columnVisibilityButtonRef.current) {
                      const rect = columnVisibilityButtonRef.current.getBoundingClientRect();
                      setColumnVisibilityPosition({
                        top: rect.bottom + window.scrollY + 8,
                        right: window.innerWidth - rect.right + window.scrollX
                      });
                    }
                    setShowColumnVisibility(!showColumnVisibility);
                  }}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
                  title="Column Visibility"
                >
                  <Squares2X2Icon className="h-5 w-5" />
                </button>
              </div>
              
              {/* Column Visibility Dropdown */}
              {showColumnVisibility && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowColumnVisibility(false)}
                  />
                  <div 
                    className="fixed w-56 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-96 overflow-y-auto column-visibility-dropdown-content"
                    style={{
                      top: columnVisibilityPosition.top ? `${columnVisibilityPosition.top}px` : 'auto',
                      right: columnVisibilityPosition.right ? `${columnVisibilityPosition.right}px` : '16px',
                      bottom: columnVisibilityPosition.top ? 'auto' : '16px'
                    }}
                  >
                    <div className="p-2">
                      <div className="text-xs font-semibold text-gray-700 mb-2 px-2">Column Visibility</div>
                      {getAllTableFields().map((field) => (
                        <label 
                          key={field.key} 
                          className="flex items-center px-2 py-1 hover:bg-gray-50 rounded cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={columnVisibility[field.key] !== false}
                            onChange={(e) => setColumnVisibility(prev => ({ ...prev, [field.key]: e.target.checked }))}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700">{field.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Export Buttons */}
              <ExportButtonsWithAll
                data={tasks.map(task => ({
                  ...task,
                  assignedToNames: [
                    ...(task.assignedTo || []).map(u => `${u.firstName} ${u.lastName}`),
                    ...(task.assignedGroups || []).map(g => `Group: ${g.name}`)
                  ].join(', '),
                  deadline: task.deadline ? new Date(task.deadline).toLocaleDateString() : 'N/A'
                }))}
                fileName="Tasks_Export"
                columns={[
                  { header: 'Custom ID', key: 'customId', width: 15 },
                  { header: 'Task Name', key: 'name', width: 25 },
                  { header: 'Client', key: 'client.clientName', width: 20 },
                  { header: 'Assigned To', key: 'assignedToNames', width: 25 },
                  { header: 'Assigned By', key: 'assignedBy.firstName', width: 20 },
                  { header: 'Deadline', key: 'deadline', width: 15 },
                  { header: 'Status', key: 'status', width: 12 },
                  { header: 'Priority', key: 'priority', width: 12 },
                  { header: 'Description', key: 'description', width: 30 }
                ]}
                pdfOptions={{ title: 'Tasks Report', orientation: 'landscape' }}
                fetchAllData={fetchAllTasks}
                totalCount={tasks.length}
              />

              {/* Create Task Button */}
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium text-sm flex items-center space-x-2"
                onClick={() => {
                  setSelectedTask(null);
                  setIsModalVisible(true);
                }}
              >
                <PlusIcon className="h-5 w-5" />
                <span>Create Task</span>
              </button>

              {/* Pagination Arrows */}
              {pagination.total > 0 && (
                <div className="flex items-center space-x-1 border-l border-gray-300 pl-2">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                    className="p-1 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed rounded hover:bg-gray-100"
                    title="Previous"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  <span className="text-sm text-gray-600 px-2">{pagination.page} / {pagination.pages}</span>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page === pagination.pages || pagination.pages === 0}
                    className="p-1 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed rounded hover:bg-gray-100"
                    title="Next"
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Filter summary */}
        {(Object.keys(headerFilters).length > 0 || selectedStatuses.length > 0 || Object.keys(dateRangeFilters).length > 0) && (
          <div className="px-4 py-2 bg-blue-50 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 text-sm text-blue-700 flex-wrap">
                <FunnelIcon className="h-4 w-4" />
                <span>Active filters:</span>
                {selectedStatuses.length > 0 && (
                  <span className="px-2 py-1 bg-blue-100 rounded text-blue-800">
                    Status: {selectedStatuses.map(s => s.replace('_', ' ')).join(', ')}
                  </span>
                )}
                {Object.keys(headerFilters).map(key => {
                  const field = tableFields.find(f => f.key === key);
                  const filterValue = headerFilters[key];
                  
                  if (Array.isArray(filterValue) && filterValue.length > 0) {
                    let displayValues = filterValue;
                    
                    // Special handling for client and assignedBy to show names instead of IDs
                    if (key === 'client') {
                      displayValues = filterValue.map(id => {
                        const client = clients.find(c => c._id === id);
                        return client ? client.clientName : id;
                      });
                    } else if (key === 'assignedBy') {
                      displayValues = filterValue.map(id => {
                        const user = users.find(u => u._id === id);
                        return user ? `${user.firstName} ${user.lastName}` : id;
                      });
                    } else {
                      // For status and priority, format nicely
                      displayValues = filterValue.map(v => v.replace('_', ' '));
                    }
                    
                    return (
                      <span key={key} className="px-2 py-1 bg-blue-100 rounded text-blue-800">
                        {field?.label}: {displayValues.join(', ')}
                      </span>
                    );
                  } else if (filterValue && typeof filterValue === 'string' && filterValue.trim() !== '') {
                    return (
                      <span key={key} className="px-2 py-1 bg-blue-100 rounded text-blue-800">
                        {field?.label}: "{filterValue}"
                      </span>
                    );
                  }
                  return null;
                })}
                {Object.keys(dateRangeFilters).map(key => {
                  const field = tableFields.find(f => f.key === key);
                  const range = dateRangeFilters[key];
                  if (range && (range.from || range.to)) {
                    return (
                      <span key={key} className="px-2 py-1 bg-blue-100 rounded text-blue-800">
                        {field?.label}: {range.from || '...'} to {range.to || '...'}
                      </span>
                    );
                  }
                  return null;
                })}
              </div>
              <button
                onClick={clearAllFilters}
                className="text-sm text-blue-700 hover:text-blue-900 font-medium"
              >
                Clear All
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {tableFields.map(field => (
                  <th key={field.key} className="px-6 py-3">
                    <SortableHeader field={field} onSort={handleSort} />
                  </th>
                ))}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filterLoading ? (
                <tr>
                  <td colSpan={tableFields.length + 1} className="px-6 py-12 text-center">
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  </td>
                </tr>
              ) : tasks.length === 0 ? (
                <tr>
                  <td colSpan={tableFields.length + 1} className="px-6 py-12 text-center">
                    <p className="text-gray-500">No tasks found</p>
                  </td>
                </tr>
              ) : (
                tasks.map(task => (
                  <tr key={task._id} className="hover:bg-gray-50">
                    {tableFields.map(field => (
                      <td key={field.key} className="px-6 py-4 text-sm text-gray-900">
                        {field.key === 'customId' && (
                          <span className="font-medium">{task.customId || task._id}</span>
                        )}
                        {field.key === 'name' && (
                          <button 
                            className="text-blue-600 hover:text-blue-800 font-medium"
                            onClick={() => handleTaskClick(task)}
                          >
                            {task.name}
                          </button>
                        )}
                        {field.key === 'client' && (
                          <span>{task.client?.clientName || 'N/A'}</span>
                        )}
                        {field.key === 'assignedTo' && (
                          <div className="space-y-1">
                            {task.assignedTo && task.assignedTo.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {task.assignedTo.map(user => (
                                  <span key={user._id} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                                    {user.firstName} {user.lastName}
                                  </span>
                                ))}
                              </div>
                            )}
                            {task.assignedGroups && task.assignedGroups.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {task.assignedGroups.map(group => (
                                  <span key={group._id} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                                    {group.name}
                                  </span>
                                ))}
                              </div>
                            )}
                            {(!task.assignedTo || task.assignedTo.length === 0) && 
                             (!task.assignedGroups || task.assignedGroups.length === 0) && (
                              <span className="text-gray-500 text-sm">No assignments</span>
                            )}
                          </div>
                        )}
                        {field.key === 'assignedBy' && (
                          <span>
                            {task.createdBy ? 
                              `${task.createdBy.firstName || ''} ${task.createdBy.lastName || ''}`.trim() || 
                              'Unknown' 
                              : 'Unknown'}
                          </span>
                        )}
                        {field.key === 'deadline' && (
                          <span>{task.deadline ? new Date(task.deadline).toLocaleDateString() : 'N/A'}</span>
                        )}
                        {field.key === 'status' && (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            task.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            task.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                            task.status === 'completed' ? 'bg-green-100 text-green-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {task.status.replace('_', ' ').toUpperCase()}
                          </span>
                        )}
                        {field.key === 'priority' && (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            task.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                            task.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                            task.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {task.priority?.toUpperCase() || 'N/A'}
                          </span>
                        )}
                        {field.key === 'description' && (
                          <span className="text-gray-600 truncate max-w-xs block">{task.description || 'N/A'}</span>
                        )}
                        {field.key === 'createdAt' && (
                          <span>{task.createdAt ? new Date(task.createdAt).toLocaleDateString() : 'N/A'}</span>
                        )}
                      </td>
                    ))}
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          className="p-1 text-blue-600 hover:text-blue-800"
                          onClick={() => {
                            setSelectedTask(task);
                            setIsModalVisible(true);
                          }}
                          title="Edit"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        {statusUpdatingTaskId === task._id ? (
                          <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                            <svg className="animate-spin h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Updating...
                          </span>
                        ) : (
                          <select
                            className="text-xs px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={task.status}
                            onChange={(e) => handleStatusChange(task._id, e.target.value)}
                          >
                            <option value="pending">Pending</option>
                            <option value="in_progress">In Progress</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {loading && tasks.length === 0 && (
        <div className="flex justify-center items-center mt-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Filter Dialog */}
      {filterDialog.open && (
        <FilterDialog
          field={filterDialog.field}
          onClose={() => setFilterDialog({ open: false, field: null })}
        />
      )}

      {/* Create Task Modal (with prefilling / template) */}
      <TaskCreateModal
        isOpen={isModalVisible && !selectedTask}
        onClose={() => {
          setIsModalVisible(false);
          setSelectedTask(null);
        }}
        onSuccess={() => {
          setIsModalVisible(false);
          setSelectedTask(null);
          fetchTasks();
        }}
      />

      {/* Edit Task Modal */}
      <TaskForm
        visible={isModalVisible && !!selectedTask}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedTask(null);
        }}
        onSuccess={() => {
          setIsModalVisible(false);
          setSelectedTask(null);
          fetchTasks();
        }}
        task={selectedTask}
        clients={clients}
        users={users}
        groups={groups}
      />

      {/* Verification Modal */}
      {isVerificationModalVisible && selectedTask && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-96">
            <h2 className="text-xl font-bold mb-4">Task Verification</h2>
            
            {selectedTask.verificationTypes?.map(type => {
              const verificationType = verificationTypes.find(vt => vt._id === type.id);
              return verificationType ? (
                <div key={type.id} className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">{verificationType.label}</label>
                  {verificationType.type === 'otp-email' ? (
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        maxLength={6}
                        pattern="\d{6}"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2"
                        onChange={e => {
                          const value = e.target.value;
                          setVerificationFields(prev => ({
                            ...prev,
                            ["otp"]: value
                          }));
                        }}
                        placeholder="Enter 6 digit OTP"
                      />
                      <button
                        className="mt-1 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                        onClick={async () => {
                          sendCode(selectedTask._id);
                        }}
                      >
                        Resend
                      </button>
                    </div>
                  ) : verificationType.type === 'file' ? (
                    <PasteUploadArea
                      onFileSelect={async (file) => {
                        if (file) {
                          try {
                            const formData = new FormData();
                            formData.append('file', file);
                            const response = await api.post(`/tasks/${selectedTask._id}/verification-file`, formData, {
                              headers: { 'Content-Type': 'multipart/form-data' },
                            });

                            try {
                              const verificationData = {
                                documentType: 'taskVerification',
                                documentId: selectedTask._id,
                                documentField: verificationType.label,
                                fileUrl: response.data.url,
                                fileName: file.name,
                                fileSize: file.size
                              };

                              const verificationResult = await createVerificationRecord(verificationData);
                              
                              if (verificationResult.verificationRequired) {
                                toast.info('File uploaded successfully. Verification required before approval.');
                              } else {
                                toast.success('File uploaded successfully');
                              }
                            } catch (verificationError) {
                              console.error('Verification error:', verificationError);
                              toast.success('File uploaded successfully');
                            }

                            setVerificationFields(prev => ({
                              ...prev,
                              [verificationType.label]: response.data.url
                            }));
                          } catch (error) {
                            toast.error('Failed to upload file');
                          }
                        }
                      }}
                      accept="application/pdf,image/jpeg,image/png,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      maxSize={10 * 1024 * 1024}
                      className="mt-1"
                      dragDropText="Upload a file"
                      pasteText="or paste from clipboard (Ctrl+V)"
                    >
                      <DocumentIcon className="mx-auto h-8 w-8 text-gray-400" />
                      <p className="text-sm text-gray-600">Upload a file</p>
                      <p className="text-xs text-gray-500">PDF, DOC, DOCX, JPG, PNG up to 10MB</p>
                    </PasteUploadArea>
                  ) : (
                    <input
                      type={verificationType.type || 'text'} 
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2"
                      value={verificationFields[verificationType.label] || ''}
                      onChange={e => setVerificationFields(prev => ({
                        ...prev,
                        [verificationType.label]: e.target.value
                      }))}
                    />
                  )}
                </div>
              ) : null;
            })}

            <div className="flex justify-end space-x-2 mt-4">
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                onClick={() => {
                  setIsVerificationModalVisible(false);
                  setPendingStatusChange(null);
                  setSelectedTask(null);
                  setVerificationFields({});
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                onClick={handleVerificationSubmit}
              >
                Verify & Complete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save View Dialog */}
      {showSaveViewDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Save Current View</h2>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                View Name
              </label>
              <input
                type="text"
                value={newViewName}
                onChange={(e) => setNewViewName(e.target.value)}
                placeholder="Enter view name"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>

            <div className="mb-6">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={saveAsDefault}
                  onChange={(e) => setSaveAsDefault(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Set as default view</span>
              </label>
              <p className="text-xs text-gray-500 mt-1 ml-6">
                Default view will be automatically loaded when you open this page
              </p>
            </div>

            <div className="bg-gray-50 p-3 rounded-md mb-4">
              <p className="text-sm text-gray-700 font-medium mb-2">This view will save:</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Column visibility settings</li>
                <li>• Sort field and direction</li>
              </ul>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowSaveViewDialog(false);
                  setNewViewName('');
                  setSaveAsDefault(false);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                disabled={savingView}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveView}
                disabled={savingView || !newViewName.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {savingView ? 'Saving...' : 'Save View'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Load View Dialog */}
      {showLoadViewDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Load Saved View</h2>
            
            {loadingViews ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : savedViews.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600">No saved views yet</p>
                <p className="text-sm text-gray-500 mt-2">
                  Save your current view settings to quickly access them later
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {savedViews.map((view) => (
                  <div
                    key={view._id}
                    className={`border rounded-lg p-4 hover:bg-gray-50 transition-colors ${
                      currentViewId === view._id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h3 className="font-medium text-gray-900">{view.name}</h3>
                          {view.isDefault && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                              <StarIconSolid className="h-3 w-3 mr-1" />
                              Default
                            </span>
                          )}
                          {currentViewId === view._id && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              Active
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          Created: {new Date(view.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => handleLoadView(view)}
                          className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Load
                        </button>
                        {!view.isDefault && (
                          <button
                            onClick={() => handleSetDefaultView(view._id)}
                            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                            title="Set as default"
                          >
                            <StarIcon className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteView(view._id)}
                          className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowLoadViewDialog(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskList;
